import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Gamepad2, Music, Sparkles, Languages, Calculator, PencilRuler } from "lucide-react";
import { Button } from "../components/ui/button";
import { SemearFontStyle, YHUHName } from "../components/SemearNames";

const learningLanes = [
  { icon: BookOpen, title: "Turah", text: "Learn YaHUaH's laws, statutes, commands, appointed times, and how to walk them." },
  { icon: Languages, title: "Abaryt", text: "Learn the Alap Bayt, ancient pictographs, sounds, words, and simple reading." },
  { icon: Calculator, title: "Learning", text: "ABC, numbers, parts of speech, reading, writing, and everyday learning made lively." },
  { icon: Gamepad2, title: "Activities", text: "Printable pages, matching games, challenges, memory work, puzzles, and hands-on learning." },
];

export default function YaHuaHsGirls() {
  return (
    <div className="min-h-screen bg-[#fff8ed]" data-testid="yah-girls-page">
      <SemearFontStyle />
      <section className="relative overflow-hidden border-b-4 border-kingdom-gold bg-royal-blue text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:"radial-gradient(circle at 20% 20%, white 0 2px, transparent 2px), radial-gradient(circle at 80% 60%, white 0 2px, transparent 2px)", backgroundSize:"42px 42px"}} />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-[1.1fr_.9fr] md:py-24 lg:px-8">
          <div className="self-center">
            <span className="inline-flex items-center gap-2 bg-kingdom-gold px-3 py-1.5 text-xs font-black uppercase tracking-[.18em] text-royal-blue">
              <Music className="h-4 w-4" /> YY Records &amp; Publishing
            </span>
            <h1 className="mt-5 font-heading text-4xl font-black leading-tight sm:text-6xl">YaHUaH&apos;s Girls</h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/90">
              A lively learning and music space where sisters teach Turah, YaHUaH&apos;s laws, statutes and commands, Abaryt, reading, numbers, language and more through songs, movement, stories and hands-on learning.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/ancient-path-youth"><Button className="bg-kingdom-gold text-royal-blue hover:bg-kingdom-gold/90">Ancient Path Youth <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/free-resources"><Button variant="outline" className="border-white text-white hover:bg-white/10">Activities</Button></Link>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="relative flex aspect-square w-full max-w-sm items-center justify-center overflow-hidden rounded-full border-8 border-white/25 bg-gradient-to-br from-kingdom-gold via-[#f8d978] to-[#e890b8] shadow-2xl">
              <div className="absolute inset-8 rounded-full border-2 border-white/50" />
              <div className="relative text-center text-royal-blue">
                <Sparkles className="mx-auto mb-3 h-10 w-10" />
                <div className="font-heading text-5xl font-black">YG</div>
                <div className="mt-2 text-sm font-black uppercase tracking-[.2em]">temporary art</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="mb-9 text-center">
          <span className="text-xs font-black uppercase tracking-[.2em] text-kingdom-gold">Learn · Sing · Move · Make</span>
          <h2 className="mt-2 font-heading text-3xl font-black text-royal-blue sm:text-4xl">Learning should feel alive.</h2>
          <p className="mx-auto mt-3 max-w-3xl text-slate-600">Short lessons, memorable songs, movement, repetition, games and projects give serious learning energy without turning it into dull worksheet time.</p>
        </div>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {learningLanes.map(({icon:Icon,title,text}) => (
            <div key={title} className="border-2 border-royal-blue/10 bg-white p-6 shadow-sm">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-kingdom-gold/15 text-royal-blue"><Icon className="h-6 w-6" /></div>
              <h3 className="font-heading text-xl font-bold text-royal-blue">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#f4ead8] py-14">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
          <Link to="/free-resources/alap-bayt" className="group bg-white p-7 shadow-sm ring-1 ring-black/5 hover:-translate-y-1 transition-transform">
            <div className="flex items-center justify-between gap-4">
              <div><span className="text-xs font-black uppercase tracking-[.2em] text-kingdom-gold">Featured activity</span><h3 className="mt-2 font-heading text-3xl font-black text-royal-blue">Alap Bayt</h3><p className="mt-2 text-slate-600">Explore the ancient letters one at a time with pictographs, sounds, numbers and learning prompts.</p></div>
              <span className="text-5xl font-bold text-royal-blue">𐤀</span>
            </div>
            <span className="mt-5 inline-flex items-center font-bold text-royal-blue">Open Alap Bayt <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </Link>
          <Link to="/free-resources" className="group bg-royal-blue p-7 text-white shadow-sm hover:-translate-y-1 transition-transform">
            <PencilRuler className="h-10 w-10 text-kingdom-gold" />
            <h3 className="mt-4 font-heading text-3xl font-black">Activity Room</h3>
            <p className="mt-2 text-white/80">The home for printouts, games, matching activities, practice sheets and new learning packs as they are added.</p>
            <span className="mt-5 inline-flex items-center font-bold text-kingdom-gold">See activities <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
          </Link>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-heading text-3xl font-black text-royal-blue">Connected to the Free Resource Library</h2>
          <p className="mt-4 text-slate-600">YaHUaH&apos;s Girls connects directly into the Free Resources library, while Ancient Path Youth remains its own focused learning path. Songs and teaching videos can point learners to matching lessons, activities and printables without making the entire library part of the artist page.</p>
          <p className="mt-3 text-slate-600">The shared library can serve early learners, youth, teens, adults and whole households, while this page remains focused on the artist group and its teaching content for <YHUHName />.</p>
        </div>
      </section>
    </div>
  );
}
